/**
 * <LiveReportCard> — one past live session's report in the Rapports tab (organism).
 *
 * The same anatomy as « Dernier bilan » on the home: `<LiveReportHeader>` (chapter chips and
 * Remplacement / Rattrapage chips, the tutor's capsule, date · time · tutor, the engagement ring and
 * its word), then two lines of the tutor's own words when there are some. The WHOLE card opens the
 * report, a chevron says so — no "Voir le rapport complet" button. No aurora, no gradient frame: the
 * aurora is the home's moment, and a list of reports has no single card to single out.
 *
 * **A missed session** (`isAbsent`) is a short greyed line — « Absent · Jeudi 17 septembre · avec
 * Camille » — with nothing to open: the detail of an absence only repeats the date over a message to
 * the student (`liveReportDetailsAdapter`, `ABSENT_HERO`), so the line is not pressable, as the
 * legacy card had no button either.
 *
 * Composes DS atoms/molecules only — no business logic, no navigation (events go up via `onOpenReport`).
 */
import React, { useContext } from "react"
import { StyleSheet } from "react-native-unistyles"

import { DimensionsContext } from "../../../common/utils/mobileUtils"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Icon } from "../../atoms/icons/Icon"
import { Text } from "../../atoms/typography/Text"
import { Card } from "../../molecules/Card"
import { LiveReportHeader } from "../../molecules/LiveReportHeader"
import { breakpoints } from "../../theme/tokens/breakpoints"
import { type UILiveSessionReport } from "../../../ports/primary/ui/types"

export type LiveReportCardProps = {
    report: UILiveSessionReport
    /** Opens the report — the whole card is the target. Never wired on an absence. */
    onOpenReport?: () => void
    testID?: string
}

/** La largeur de la carte : la colonne moins ses marges sous `md`, 600 au-delà. */
export const CARD_MAX_WIDTH = 600
export const NARROW_MARGIN = 40
export const WIDE_FROM = breakpoints.md

/** La largeur d'une carte de l'onglet Rapports, lue sur la colonne — exportée pour son squelette. */
export function useLiveReportCardWidth(): number {
    const columnWidth = useContext(DimensionsContext)?.width ?? 0
    return columnWidth < WIDE_FROM ? columnWidth - NARROW_MARGIN : CARD_MAX_WIDTH
}

export function LiveReportCard(props: LiveReportCardProps) {
    const { report, onOpenReport, testID } = props
    const cardWidth = useLiveReportCardWidth()

    if (report.isAbsent) {
        return (
            <Card p="4" testID={testID} style={[styles.card(cardWidth), styles.short]}>
                <LiveReportHeader report={report} />
            </Card>
        )
    }

    return (
        <Card
            pressable={onOpenReport != null}
            onPress={onOpenReport}
            p="4"
            testID={testID}
            style={styles.card(cardWidth)}
        >
            <Row gap="3" align="center">
                <Stack gap="3" style={styles.body}>
                    <LiveReportHeader report={report} />
                    {report.comment != null && (
                        <Text size="text-lg" numberOfLines={2}>
                            « {report.comment} »
                        </Text>
                    )}
                </Stack>

                {onOpenReport != null && <Icon name="chevron-right" size={20} color="text-muted" />}
            </Row>
        </Card>
    )
}

const styles = StyleSheet.create((theme) => ({
    card: (width: number) => ({
        width,
        maxWidth: CARD_MAX_WIDTH,
        alignSelf: "center" as const,
    }),
    body: {
        flex: 1,
        flexShrink: 1,
    },
    /** La ligne d'une absence : le retrait latéral des autres cartes, pour que les capsules s'alignent. */
    short: {
        paddingVertical: theme.spacing["3"],
    },
}))
